import { getUUID } from '@mybonus/public';
import constate from 'constate';
import { useState, useEffect } from 'react';

export type ToastOpts = {
	type: 'success' | 'warning' | 'error';
	timeout: number | 'never';
	message: string;
	details?: string;
	onDismiss?: () => void;
};

export type ToastInstance = {
	opts: ToastOpts;
	time: number;
	id: string;
	isDismissing: boolean;
};

export type UseToastsOpts = {
	animationDuration?: number;
};

function useToasts(cfg: UseToastsOpts) {
	const animationDuration = cfg.animationDuration || 500;
	const [toasts, setToasts] = useState<ToastInstance[]>([]);

	function addToast(opts: ToastOpts) {
		const newToast = {
			time: Date.now(),
			id: getUUID(),
			opts,
			isDismissing: false,
		};

		setToasts((prevToasts) => [...prevToasts, newToast]);

		if (opts.timeout !== 'never') {
			setTimeout(() => dismissToast(newToast.id), opts.timeout);
		}
	}

	function dismissToast(id: string) {
		setToasts((prevToasts) =>
			prevToasts.map((t) => ({
				...t,
				isDismissing: t.id === id,
			})),
		);

		setTimeout(() => {
			const removeToast = toasts.find((t) => t.id === id);
			removeToast?.opts.onDismiss?.();

			setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id));
		}, animationDuration);
	}

	function dismissAll() {
		toasts.forEach((toast) => dismissToast(toast.id));
	}

	const currentToast = toasts[0];

	return {
		list: toasts,
		currentToast,
		addToast,
		dismissToast,
		dismissAll,
		animationDuration,
	};
}

export const [ToastsProvider, useToastsContext] = constate(useToasts);
